import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import IUniswapV2PairJson from '@kodiak-finance/v2-core/build/IUniswapV2Pair.json'
import CHARM_PERIPHERY_ABI from 'abis/CharmPeriphery.json'
import CHARM_VAULT_ABI from 'abis/CharmVault.json'
import ISLAND_ABI from 'abis/island.json'
import { REFETCH_POLICY_FAST } from 'config/RefetchPolicies'
import { CHARM_PERIPHERY_ADDRESSES } from 'constants/addresses'
import JSBI from 'jsbi'
import useCurrencyBalance from 'lib/hooks/useCurrencyBalance'
import { useMemo, useState } from 'react'
import { IslandApiResponse } from 'state/data/slice'
import { unwrappedToken } from 'utils/unwrappedToken'
import type { Abi } from 'viem'

import { useToken } from './Tokens'
import { useConnection } from './useConnection'
import useIslandTVL from './useIslandTVL'
import { useReadContractMultiple } from './useReadContractMultiple'

const { abi: IUniswapV2PairABI } = IUniswapV2PairJson
const DENOMINATOR = JSBI.BigInt('100000000000000000000000')

export function isIslandPool(pool: IslandApiResponse) {
  return pool.provider === 'kodiak'
}

export function isPairPool(pool: IslandApiResponse) {
  return pool.provider === 'v2'
}

export function isCharmPool(pool: IslandApiResponse) {
  return pool.provider === 'charm'
}

function usePoolPositionDetails(
  pool: IslandApiResponse | null,
  currencyA: Currency | null,
  currencyB: Currency | null,
  account: any
) {
  const { chainId } = useConnection()
  const _isIslandPool = pool && isIslandPool(pool)
  const _isPairPool = pool && isPairPool(pool)
  const _isCharmPool = pool && isCharmPool(pool)
  const charmPeripheryAddress = CHARM_PERIPHERY_ADDRESSES[chainId]

  const poolToken = useToken(pool?.id)
  const poolCurrency = (poolToken && unwrappedToken(poolToken)) || undefined

  const islandContracts = useMemo(() => {
    if (!_isIslandPool || !pool?.id) return []
    return [
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'getUnderlyingBalances',
      },
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isIslandPool, pool?.id])

  // Use Wagmi for island contract reads
  const { data: islandData, error: islandDataError } = useReadContractMultiple({
    contracts: islandContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: islandContracts.length > 0,
    },
  })

  const islandUnderlyingBalances = useMemo(
    () =>
      islandData?.[0]?.result
        ? {
            amount0Current: (islandData[0].result as [bigint, bigint])[0],
            amount1Current: (islandData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [islandData]
  )
  const islandTotalSupply = islandData?.[1]?.result as bigint

  const v2Contracts = useMemo(() => {
    if (!_isPairPool || !pool?.id) return []
    return [
      {
        address: pool?.id,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'getReserves',
      },
      {
        address: pool?.id,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isPairPool, pool?.id])

  // Use Wagmi for V2 pair contract reads
  const { data: v2Data, error: v2DataError } = useReadContractMultiple({
    contracts: v2Contracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: v2Contracts.length > 0,
    },
  })

  const v2PoolUnderlyingBalances = useMemo(
    () =>
      v2Data?.[0]?.result
        ? {
            amount0Current: (v2Data[0].result as [bigint, bigint, number])[0],
            amount1Current: (v2Data[0].result as [bigint, bigint, number])[1],
          }
        : undefined,
    [v2Data]
  )
  const v2TotalSupply = v2Data?.[1]?.result as bigint

  const charmContracts = useMemo(() => {
    if (!_isCharmPool || !pool?.id || !charmPeripheryAddress) return []
    return [
      {
        address: charmPeripheryAddress,
        abi: CHARM_PERIPHERY_ABI as Abi,
        functionName: 'getVaultAmounts',
        args: [pool.id],
      },
      {
        address: pool.id,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isCharmPool, pool?.id, charmPeripheryAddress])

  // Use Wagmi for Charm vault contract reads
  const { data: charmData, error: charmDataError } = useReadContractMultiple({
    contracts: charmContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: charmContracts.length > 0,
    },
  })

  const charmUnderlyingBalances = useMemo(
    () =>
      charmData?.[0]?.result
        ? {
            amount0Current: (charmData[0].result as [bigint, bigint])[0],
            amount1Current: (charmData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [charmData]
  )
  const charmTotalSupply = charmData?.[1]?.result as bigint

  const poolUnderlyingBalances = useMemo(() => {
    if (pool) {
      if (_isIslandPool) {
        return islandUnderlyingBalances
      } else if (_isPairPool) {
        return v2PoolUnderlyingBalances
      } else if (_isCharmPool) {
        return charmUnderlyingBalances
      }
    }
    return undefined
  }, [
    pool,
    _isIslandPool,
    _isPairPool,
    _isCharmPool,
    islandUnderlyingBalances,
    v2PoolUnderlyingBalances,
    charmUnderlyingBalances,
  ])

  const userBalanceShares = useCurrencyBalance(account, poolCurrency, REFETCH_POLICY_FAST)

  const totalSupply = useMemo(() => {
    if (!poolCurrency) return undefined

    if (_isIslandPool) {
      if (!islandTotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, islandTotalSupply.toString())
    }

    if (_isPairPool) {
      if (!v2TotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, v2TotalSupply.toString())
    }

    if (_isCharmPool) {
      if (!charmTotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, charmTotalSupply.toString())
    }

    return undefined
  }, [poolCurrency, islandTotalSupply, v2TotalSupply, charmTotalSupply, _isIslandPool, _isPairPool, _isCharmPool])

  const amount0Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (poolUnderlyingBalances) {
      return currencyA && poolUnderlyingBalances?.amount0Current
        ? CurrencyAmount.fromRawAmount(currencyA, poolUnderlyingBalances.amount0Current.toString())
        : undefined
    } else {
      return undefined
    }
  }, [currencyA, poolUnderlyingBalances])

  const amount1Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (poolUnderlyingBalances) {
      return currencyB && poolUnderlyingBalances?.amount1Current
        ? CurrencyAmount.fromRawAmount(currencyB, poolUnderlyingBalances.amount1Current.toString())
        : undefined
    }
    return undefined
  }, [currencyB, poolUnderlyingBalances])

  const userLiquidityPercentage =
    userBalanceShares && totalSupply && !JSBI.equal(totalSupply.quotient, JSBI.BigInt('0'))
      ? new Percent(
          JSBI.divide(JSBI.multiply(userBalanceShares.quotient, DENOMINATOR), totalSupply.quotient),
          DENOMINATOR
        )
      : undefined

  const userAmount0 =
    poolUnderlyingBalances && userLiquidityPercentage && amount0Current
      ? userLiquidityPercentage.multiply(amount0Current?.quotient).quotient
      : undefined
  const userAmount1 =
    poolUnderlyingBalances && userLiquidityPercentage && amount1Current
      ? userLiquidityPercentage.multiply(amount1Current?.quotient).quotient
      : undefined

  const userUnderlyingBalances = {
    amount0:
      currencyA && userAmount0 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyA, userAmount0)
        : undefined,
    amount1:
      currencyB && userAmount1 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyB, userAmount1)
        : undefined,
  }

  const onChainTVL = useIslandTVL(_isIslandPool ? pool?.id : undefined, pool?.token0?.id, pool?.token1?.id)

  const userBalanceSharesUSD = useMemo(() => {
    if (!userBalanceShares || !pool) return 0
    if (!totalSupply || JSBI.equal(totalSupply.quotient, JSBI.BigInt('0'))) return 0

    const userSharePercentage = Number(userBalanceShares.toExact()) / Number(totalSupply.toExact())

    if (_isIslandPool) {
      if (onChainTVL > 0) {
        return onChainTVL * userSharePercentage
      }
      return 0
    }

    if (_isPairPool) {
      if (amount0Current && amount1Current && pool?.token0?.price && pool?.token1?.price) {
        const reserve0 = Number(amount0Current.toExact())
        const reserve1 = Number(amount1Current.toExact())

        const token0PriceUSD = pool.token0.price
        const token1PriceUSD = pool.token1.price

        const manualReserveUSD = reserve0 * token0PriceUSD + reserve1 * token1PriceUSD

        return manualReserveUSD * userSharePercentage
      }
      return 0
    }

    if (_isCharmPool) {
      if (amount0Current && amount1Current && pool?.token0?.price && pool?.token1?.price) {
        const total0 = Number(amount0Current.toExact())
        const total1 = Number(amount1Current.toExact())

        const token0PriceUSD = pool.token0.price
        const token1PriceUSD = pool.token1.price

        const totalValueUSD = total0 * token0PriceUSD + total1 * token1PriceUSD

        return totalValueUSD * userSharePercentage
      }
      return 0
    }

    return 0
  }, [
    userBalanceShares,
    pool,
    totalSupply,
    _isIslandPool,
    _isPairPool,
    _isCharmPool,
    onChainTVL,
    amount0Current,
    amount1Current,
  ])

  const error = useMemo(() => {
    if (_isIslandPool) return islandDataError
    if (_isPairPool) return v2DataError
    if (_isCharmPool) return charmDataError
    return null
  }, [_isIslandPool, _isPairPool, _isCharmPool, islandDataError, v2DataError, charmDataError])

  return {
    error,
    poolCurrency,
    totalSupply,
    poolUnderlyingBalances,
    userBalanceShares,
    userBalanceSharesUSD,
    userUnderlyingBalances,
    userLiquidityPercentage,
    pool,
  }
}

export const usePoolUnderlyingBalances = (
  pool: IslandApiResponse | null,
  userBalanceShares: CurrencyAmount<Token> | undefined | null,
  currencyA?: Currency | null,
  currencyB?: Currency | null
) => {
  const { chainId } = useConnection()
  const poolToken = useToken(pool?.id)
  const poolCurrency = (poolToken && unwrappedToken(poolToken)) || undefined
  const charmPeripheryAddress = CHARM_PERIPHERY_ADDRESSES[chainId]

  const _isIslandPool = pool && isIslandPool(pool)
  const _isPairPool = pool && isPairPool(pool)
  const _isCharmPool = pool && isCharmPool(pool)

  // Use Wagmi for island contract reads
  const { data: islandData } = useReadContractMultiple({
    contracts: [
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'getUnderlyingBalances',
      },
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'totalSupply',
      },
    ],
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!_isIslandPool && !!pool?.id,
    },
  })

  const islandUnderlyingBalances = useMemo(
    () =>
      islandData?.[0]?.result
        ? {
            amount0Current: (islandData[0].result as [bigint, bigint])[0],
            amount1Current: (islandData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [islandData]
  )
  const islandTotalSupply = islandData?.[1]?.result as bigint

  // Use Wagmi for V2 pair contract reads
  const { data: v2Data } = useReadContractMultiple({
    contracts: [
      {
        address: _isPairPool ? pool?.id : undefined,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'getReserves',
      },
      {
        address: _isPairPool ? pool?.id : undefined,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'totalSupply',
      },
    ],
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: !!_isPairPool && !!pool?.id,
    },
  })

  const v2PoolUnderlyingBalances = useMemo(
    () =>
      v2Data?.[0]?.result
        ? {
            amount0Current: (v2Data[0].result as [bigint, bigint, number])[0],
            amount1Current: (v2Data[0].result as [bigint, bigint, number])[1],
          }
        : undefined,
    [v2Data]
  )
  const v2TotalSupply = v2Data?.[1]?.result as bigint
  const charmContracts = useMemo(() => {
    if (!_isCharmPool || !pool?.id || !charmPeripheryAddress) return []
    return [
      {
        address: charmPeripheryAddress,
        abi: CHARM_PERIPHERY_ABI as Abi,
        functionName: 'getVaultAmounts',
        args: [pool.id],
      },
      {
        address: pool.id,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isCharmPool, pool?.id, charmPeripheryAddress])

  // Use Wagmi for Charm vault contract reads
  const { data: charmData } = useReadContractMultiple({
    contracts: charmContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: charmContracts.length > 0,
    },
  })

  const charmUnderlyingBalances = useMemo(
    () =>
      charmData?.[0]?.result
        ? {
            amount0Current: (charmData[0].result as [bigint, bigint])[0],
            amount1Current: (charmData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [charmData]
  )
  const charmTotalSupply = charmData?.[1]?.result as bigint

  const poolUnderlyingBalances = useMemo(() => {
    if (pool) {
      if (isIslandPool(pool)) {
        return islandUnderlyingBalances
      } else if (isPairPool(pool)) {
        return v2PoolUnderlyingBalances
      } else if (isCharmPool(pool)) {
        return charmUnderlyingBalances
      }
    }
    return undefined
  }, [pool, islandUnderlyingBalances, v2PoolUnderlyingBalances, charmUnderlyingBalances])

  const totalSupply = useMemo(() => {
    if (!poolCurrency) return undefined

    if (_isIslandPool) {
      if (!islandTotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, islandTotalSupply.toString())
    }

    if (_isPairPool) {
      if (!v2TotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, v2TotalSupply.toString())
    }

    if (_isCharmPool) {
      if (!charmTotalSupply) return undefined
      return CurrencyAmount.fromRawAmount(poolCurrency, charmTotalSupply.toString())
    }

    return undefined
  }, [poolCurrency, islandTotalSupply, v2TotalSupply, charmTotalSupply, _isIslandPool, _isPairPool, _isCharmPool])

  const userLiquidityPercentage =
    userBalanceShares && totalSupply && !JSBI.equal(totalSupply.quotient, JSBI.BigInt('0'))
      ? new Percent(
          JSBI.divide(JSBI.multiply(userBalanceShares.quotient, DENOMINATOR), totalSupply.quotient),
          DENOMINATOR
        )
      : undefined

  const amount0Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (poolUnderlyingBalances) {
      return currencyA && poolUnderlyingBalances.amount0Current
        ? CurrencyAmount.fromRawAmount(currencyA, poolUnderlyingBalances.amount0Current.toString())
        : undefined
    }
    return undefined
  }, [currencyA, poolUnderlyingBalances])

  const amount1Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (poolUnderlyingBalances) {
      return currencyB && poolUnderlyingBalances.amount1Current
        ? CurrencyAmount.fromRawAmount(currencyB, poolUnderlyingBalances.amount1Current.toString())
        : undefined
    }
    return undefined
  }, [currencyB, poolUnderlyingBalances])

  const userAmount0 =
    poolUnderlyingBalances && userLiquidityPercentage && amount0Current
      ? userLiquidityPercentage.multiply(amount0Current?.quotient).quotient
      : undefined
  const userAmount1 =
    poolUnderlyingBalances && userLiquidityPercentage && amount1Current
      ? userLiquidityPercentage.multiply(amount1Current?.quotient).quotient
      : undefined

  const userUnderlyingBalances = {
    amount0:
      currencyA && userAmount0 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyA, userAmount0)
        : undefined,
    amount1:
      currencyB && userAmount1 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyB, userAmount1)
        : undefined,
  }

  return userUnderlyingBalances
}

/**
 * Optimized version that uses cached underlying balance data
 * instead of making duplicate expensive contract calls
 */
export function useOptimizedPoolPositionDetails(
  pool: IslandApiResponse | null,
  currencyA: Currency | null,
  currencyB: Currency | null,
  account: any,
  sharedBalances: SharedUnderlyingBalances
) {
  const poolToken = useToken(pool?.id)
  const poolCurrency = (poolToken && unwrappedToken(poolToken)) || undefined

  const [error, setError] = useState<Error | null>(null)

  const userBalanceShares = useCurrencyBalance(account, poolCurrency, REFETCH_POLICY_FAST)

  // Extract data from shared cache instead of making new contract calls
  const { amount0Data, amount1Data, totalSupply, isLoading } = useMemo(() => {
    if (sharedBalances.poolType === 'island' && sharedBalances.island) {
      return {
        amount0Data: sharedBalances.island.amount0Current,
        amount1Data: sharedBalances.island.amount1Current,
        totalSupply: sharedBalances.island.totalSupply,
        isLoading: sharedBalances.island.isLoading,
      }
    }

    if (sharedBalances.poolType === 'v2' && sharedBalances.pair) {
      return {
        amount0Data: sharedBalances.pair.reserve0,
        amount1Data: sharedBalances.pair.reserve1,
        totalSupply: sharedBalances.pair.totalSupply,
        isLoading: sharedBalances.pair.isLoading,
      }
    }

    if (sharedBalances.poolType === 'charm' && sharedBalances.charm) {
      return {
        amount0Data: sharedBalances.charm.total0,
        amount1Data: sharedBalances.charm.total1,
        totalSupply: sharedBalances.charm.totalSupply,
        isLoading: sharedBalances.charm.isLoading,
      }
    }

    return {
      amount0Data: undefined,
      amount1Data: undefined,
      totalSupply: undefined,
      isLoading: false,
    }
  }, [sharedBalances])

  const totalSupplyCurrency = useMemo(() => {
    if (!poolCurrency || !totalSupply) return undefined
    return CurrencyAmount.fromRawAmount(poolCurrency, totalSupply.toString())
  }, [poolCurrency, totalSupply])

  const amount0Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    return currencyA && amount0Data ? CurrencyAmount.fromRawAmount(currencyA, amount0Data.toString()) : undefined
  }, [currencyA, amount0Data])

  const amount1Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    return currencyB && amount1Data ? CurrencyAmount.fromRawAmount(currencyB, amount1Data.toString()) : undefined
  }, [currencyB, amount1Data])

  const userLiquidityPercentage =
    userBalanceShares && totalSupplyCurrency && !JSBI.equal(totalSupplyCurrency.quotient, JSBI.BigInt('0'))
      ? new Percent(
          JSBI.divide(JSBI.multiply(userBalanceShares.quotient, DENOMINATOR), totalSupplyCurrency.quotient),
          DENOMINATOR
        )
      : undefined

  const userAmount0 =
    amount0Data && userLiquidityPercentage && amount0Current
      ? userLiquidityPercentage.multiply(amount0Current?.quotient).quotient
      : undefined
  const userAmount1 =
    amount1Data && userLiquidityPercentage && amount1Current
      ? userLiquidityPercentage.multiply(amount1Current?.quotient).quotient
      : undefined

  const userUnderlyingBalances = {
    amount0:
      currencyA && userAmount0 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyA, userAmount0)
        : undefined,
    amount1:
      currencyB && userAmount1 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyB, userAmount1)
        : undefined,
  }

  const onChainTVL = useIslandTVL(
    sharedBalances.poolType === 'island' ? pool?.id : undefined,
    pool?.token0?.id,
    pool?.token1?.id,
    sharedBalances.island ? [sharedBalances.island.amount0Current, sharedBalances.island.amount1Current] : undefined
  )

  const userBalanceSharesUSD = useMemo(() => {
    if (!userBalanceShares || !pool) return 0
    if (!totalSupplyCurrency || JSBI.equal(totalSupplyCurrency.quotient, JSBI.BigInt('0'))) return 0

    const userSharePercentage = Number(userBalanceShares.toExact()) / Number(totalSupplyCurrency.toExact())

    if (sharedBalances.poolType === 'island') {
      if (onChainTVL > 0) {
        return onChainTVL * userSharePercentage
      }
      return 0
    }

    if (sharedBalances.poolType === 'v2') {
      if (amount0Current && amount1Current && pool?.token0?.price && pool?.token1?.price) {
        const reserve0 = Number(amount0Current.toExact())
        const reserve1 = Number(amount1Current.toExact())

        const token0PriceUSD = pool.token0.price
        const token1PriceUSD = pool.token1.price

        const manualReserveUSD = reserve0 * token0PriceUSD + reserve1 * token1PriceUSD

        return manualReserveUSD * userSharePercentage
      }
      return 0
    }

    if (sharedBalances.poolType === 'charm') {
      if (amount0Current && amount1Current && pool?.token0?.price && pool?.token1?.price) {
        const total0 = Number(amount0Current.toExact())
        const total1 = Number(amount1Current.toExact())

        const token0PriceUSD = pool.token0.price
        const token1PriceUSD = pool.token1.price

        const totalValueUSD = total0 * token0PriceUSD + total1 * token1PriceUSD

        return totalValueUSD * userSharePercentage
      }
      return 0
    }

    return 0
  }, [
    userBalanceShares,
    pool,
    totalSupplyCurrency,
    sharedBalances.poolType,
    onChainTVL,
    amount0Current,
    amount1Current,
  ])

  return {
    error,
    poolCurrency,
    totalSupply: totalSupplyCurrency,
    userBalanceShares,
    userBalanceSharesUSD,
    userUnderlyingBalances,
    userLiquidityPercentage,
    pool,
    isLoading,
  }
}

/**
 * Optimized version that uses cached underlying balance data
 * Eliminates duplicate expensive contract calls
 */
export const useOptimizedPoolUnderlyingBalances = (
  pool: IslandApiResponse | null,
  userBalanceShares: CurrencyAmount<Token> | undefined | null,
  sharedBalances: SharedUnderlyingBalances, // Injected cached data
  currencyA?: Currency | null,
  currencyB?: Currency | null
) => {
  const poolToken = useToken(pool?.id)
  const poolCurrency = (poolToken && unwrappedToken(poolToken)) || undefined

  // Extract data from shared cache instead of making new contract calls
  const { amount0Data, amount1Data, totalSupply } = useMemo(() => {
    if (sharedBalances.poolType === 'island' && sharedBalances.island) {
      return {
        amount0Data: sharedBalances.island.amount0Current,
        amount1Data: sharedBalances.island.amount1Current,
        totalSupply: sharedBalances.island.totalSupply,
      }
    }

    if (sharedBalances.poolType === 'v2' && sharedBalances.pair) {
      return {
        amount0Data: sharedBalances.pair.reserve0,
        amount1Data: sharedBalances.pair.reserve1,
        totalSupply: sharedBalances.pair.totalSupply,
      }
    }

    if (sharedBalances.poolType === 'charm' && sharedBalances.charm) {
      return {
        amount0Data: sharedBalances.charm.total0,
        amount1Data: sharedBalances.charm.total1,
        totalSupply: sharedBalances.charm.totalSupply,
      }
    }

    return {
      amount0Data: undefined,
      amount1Data: undefined,
      totalSupply: undefined,
    }
  }, [sharedBalances])

  const totalSupplyCurrency = useMemo(() => {
    if (!poolCurrency || !totalSupply) return undefined
    return CurrencyAmount.fromRawAmount(poolCurrency, totalSupply.toString())
  }, [poolCurrency, totalSupply])

  const userLiquidityPercentage =
    userBalanceShares && totalSupplyCurrency && !JSBI.equal(totalSupplyCurrency.quotient, JSBI.BigInt('0'))
      ? new Percent(
          JSBI.divide(JSBI.multiply(userBalanceShares.quotient, DENOMINATOR), totalSupplyCurrency.quotient),
          DENOMINATOR
        )
      : undefined

  const amount0Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    return currencyA && amount0Data ? CurrencyAmount.fromRawAmount(currencyA, amount0Data.toString()) : undefined
  }, [currencyA, amount0Data])

  const amount1Current: CurrencyAmount<Currency> | undefined = useMemo(() => {
    return currencyB && amount1Data ? CurrencyAmount.fromRawAmount(currencyB, amount1Data.toString()) : undefined
  }, [currencyB, amount1Data])

  const userAmount0 =
    amount0Data && userLiquidityPercentage && amount0Current
      ? userLiquidityPercentage.multiply(amount0Current?.quotient).quotient
      : undefined
  const userAmount1 =
    amount1Data && userLiquidityPercentage && amount1Current
      ? userLiquidityPercentage.multiply(amount1Current?.quotient).quotient
      : undefined

  const userUnderlyingBalances = {
    amount0:
      currencyA && userAmount0 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyA, userAmount0)
        : undefined,
    amount1:
      currencyB && userAmount1 && userLiquidityPercentage
        ? CurrencyAmount.fromRawAmount(currencyB, userAmount1)
        : undefined,
  }

  return userUnderlyingBalances
}

export default usePoolPositionDetails

export interface SharedUnderlyingBalances {
  island?: {
    amount0Current: any
    amount1Current: any
    totalSupply: any
    isLoading: boolean
  }
  pair?: {
    reserve0: any
    reserve1: any
    totalSupply: any
    isLoading: boolean
  }
  charm?: {
    total0: any
    total1: any
    totalSupply: any
    isLoading: boolean
  }
  poolType: 'island' | 'v2' | 'charm' | null
}

/**
 * Single source of truth for expensive underlying balance calls
 * Replaces multiple parallel calls with one cached result
 */
export function useSharedUnderlyingBalances(pool: IslandApiResponse | null): SharedUnderlyingBalances {
  const { chainId } = useConnection()
  const _isIslandPool = pool && isIslandPool(pool)
  const _isPairPool = pool && isPairPool(pool)
  const _isCharmPool = pool && isCharmPool(pool)
  const charmPeripheryAddress = CHARM_PERIPHERY_ADDRESSES[chainId]

  const islandContracts = useMemo(() => {
    if (!_isIslandPool || !pool?.id) return []
    return [
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'getUnderlyingBalances',
      },
      {
        address: pool?.id,
        abi: ISLAND_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isIslandPool, pool?.id])

  // Island calls using Wagmi
  const { data: islandData, isLoading: islandDataLoading } = useReadContractMultiple({
    contracts: islandContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: islandContracts.length > 0,
    },
  })

  const islandUnderlyingBalances = useMemo(
    () =>
      islandData?.[0]?.result
        ? {
            amount0Current: (islandData[0].result as [bigint, bigint])[0],
            amount1Current: (islandData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [islandData]
  )

  const islandTotalSupply = islandData?.[1]?.result as bigint | undefined

  const v2Contracts = useMemo(() => {
    if (!_isPairPool || !pool?.id) return []
    return [
      {
        address: pool?.id,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'getReserves',
      },
      {
        address: pool?.id,
        abi: IUniswapV2PairABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isPairPool, pool?.id])

  // V2 Pair calls using Wagmi
  const { data: v2Data, isLoading: v2DataLoading } = useReadContractMultiple({
    contracts: v2Contracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: v2Contracts.length > 0,
    },
  })

  const pairReserves = useMemo(
    () =>
      v2Data?.[0]?.result
        ? {
            amount0Current: (v2Data[0].result as [bigint, bigint, number])[0],
            amount1Current: (v2Data[0].result as [bigint, bigint, number])[1],
          }
        : undefined,
    [v2Data]
  )
  const pairTotalSupply = v2Data?.[1]?.result as bigint

  const charmContracts = useMemo(() => {
    if (!_isCharmPool || !pool?.id || !charmPeripheryAddress) return []
    return [
      {
        address: charmPeripheryAddress,
        abi: CHARM_PERIPHERY_ABI as Abi,
        functionName: 'getVaultAmounts',
        args: [pool.id],
      },
      {
        address: pool.id,
        abi: CHARM_VAULT_ABI as Abi,
        functionName: 'totalSupply',
      },
    ]
  }, [_isCharmPool, pool?.id, charmPeripheryAddress])

  // Charm calls using Wagmi
  const { data: charmData, isLoading: charmDataLoading } = useReadContractMultiple({
    contracts: charmContracts,
    query: {
      ...REFETCH_POLICY_FAST,
      enabled: charmContracts.length > 0,
    },
  })

  const charmUnderlyingBalances = useMemo(
    () =>
      charmData?.[0]?.result
        ? {
            amount0Current: (charmData[0].result as [bigint, bigint])[0],
            amount1Current: (charmData[0].result as [bigint, bigint])[1],
          }
        : undefined,
    [charmData]
  )
  const charmTotalSupply = charmData?.[1]?.result as bigint

  return useMemo(() => {
    if (_isIslandPool) {
      return {
        island: {
          amount0Current: islandUnderlyingBalances?.amount0Current,
          amount1Current: islandUnderlyingBalances?.amount1Current,
          totalSupply: islandTotalSupply,
          isLoading: islandDataLoading,
        },
        poolType: 'island' as const,
      }
    }

    if (_isPairPool) {
      return {
        pair: {
          reserve0: pairReserves?.amount0Current,
          reserve1: pairReserves?.amount1Current,
          totalSupply: pairTotalSupply,
          isLoading: v2DataLoading,
        },
        poolType: 'v2' as const,
      }
    }

    if (_isCharmPool) {
      return {
        charm: {
          total0: charmUnderlyingBalances?.amount0Current,
          total1: charmUnderlyingBalances?.amount1Current,
          totalSupply: charmTotalSupply,
          isLoading: charmDataLoading,
        },
        poolType: 'charm' as const,
      }
    }

    return {
      poolType: null,
    }
  }, [
    _isIslandPool,
    _isPairPool,
    _isCharmPool,
    islandUnderlyingBalances,
    islandTotalSupply,
    islandDataLoading,
    pairReserves,
    pairTotalSupply,
    v2DataLoading,
    charmUnderlyingBalances,
    charmTotalSupply,
    charmDataLoading,
  ])
}
